import { useEffect, useMemo, useRef, useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { PILES_TREATMENT_CITIES, type PilesTreatmentCity } from "@/components/PilesTreatmentCityPage";
import { LP_CHROME_CSS } from "@/lib/lp-chrome-css";
import { notifyForm } from "@/lib/notifyForm";
import { PILES_DOCTOR_CSS, PILES_DOCTOR_HTML } from "@/lib/piles-doctor-lp";

const PAGE_FIXES = `
.lp-piles-doctor{--navy:#1b3358;--navy-2:#284a7d;--accent:#ff5757;--accent-dk:#e54848;--rose:#e3a1a0;--soft:#fafbfc;--soft-2:#f1f2f6;--white:#fff;--ink:#1a1930;--muted:#4e5072;--mute:#7a7e92;--line:#e1e3ea;--ok:#1f7a5c;--wa:#25d366;--r:14px;--shadow:0 8px 24px rgba(27,51,88,.1)}
.lp-piles-doctor[data-cta-mode="active"] .cta-off-only{display:none!important}.lp-piles-doctor[data-cta-mode="off"] .cta-active-only,.lp-piles-doctor[data-cta-mode="off"] .wa-bubble{display:none!important}
.lp-piles-doctor .doc-portrait img,.lp-piles-doctor .bio-photo img{object-position:center top}
@media(min-width:900px){.lp-piles-doctor .sticky{display:none!important}}
`;

function renderHtml(city: PilesTreatmentCity) {
  const config = PILES_TREATMENT_CITIES[city];
  const values: Record<string, string> = {
    CITY: city,
    SLUG: config.slug,
    DOCTOR_NAME: config.doctor,
    DOCTOR_WA: encodeURIComponent(config.doctor).replaceAll("%20", "+"),
    DOCTOR_IMAGE: config.doctorImage,
    DOCTOR_SHORT_QUALS: config.doctorShortQuals,
    DOCTOR_EDUCATION: config.doctorEducation,
    DOCTOR_FELLOWSHIPS: config.doctorFellowships,
    DOCTOR_CRED_1: config.doctorEducation.split("—")[0]?.trim() || "General Surgery",
    DOCTOR_CRED_2: config.doctorRole,
    DOCTOR_CRED_3: config.doctorFellowships,
    ADDRESS: config.address,
    ADDRESS_SHORT: config.addressShort,
    MAP_URL: config.mapUrl.replaceAll("&", "&amp;"),
  };
  return Object.entries(values).reduce((html, [key, value]) => html.replaceAll(`{{${key}}}`, value), PILES_DOCTOR_HTML);
}

function istHour() {
  try { return Number(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata", hour: "2-digit", hour12: false })); }
  catch { return 12; }
}

export function PilesDoctorCityPage({ city }: { city: PilesTreatmentCity }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [ctaMode, setCtaMode] = useState<"active" | "off">("active");
  const config = PILES_TREATMENT_CITIES[city];
  const html = useMemo(() => renderHtml(city), [city]);

  useEffect(() => setCtaMode(istHour() >= 9 && istHour() < 20 ? "active" : "off"), []);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];
    const leadModal = root.querySelector<HTMLElement>("#lead-modal");
    const callModal = root.querySelector<HTMLElement>("#call-modal");
    const formContainer = root.querySelector<HTMLElement>("#modal-form-container");
    const thankYou = root.querySelector<HTMLElement>("#thank-you");
    const openModal = (modal: HTMLElement | null) => { if (!modal) return; modal.classList.add("open"); modal.setAttribute("aria-hidden", "false"); document.documentElement.classList.add("modal-open"); };
    const closeModal = (modal: HTMLElement | null) => { if (!modal) return; modal.classList.remove("open"); modal.setAttribute("aria-hidden", "true"); if (!root.querySelector(".modal-backdrop.open")) document.documentElement.classList.remove("modal-open"); };
    const openLead = (doctor = config.doctor) => {
      const field = root.querySelector<HTMLInputElement>("#m-doctor");
      const page = root.querySelector<HTMLInputElement>("#m-pageurl");
      if (field) field.value = doctor;
      if (page) page.value = window.location.href;
      if (formContainer) formContainer.style.display = "";
      thankYou?.classList.remove("active");
      openModal(leadModal);
    };
    const click = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const closer = target?.closest("[data-modal-close]");
      if (closer) { event.preventDefault(); closeModal(closer.closest(".modal-backdrop") as HTMLElement | null); return; }
      const opener = target?.closest("[data-open-form]");
      if (opener) { event.preventDefault(); openLead(opener.getAttribute("data-doctor") || config.doctor); }
    };
    root.addEventListener("click", click); cleanups.push(() => root.removeEventListener("click", click));
    root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
      const handler = (event: Event) => { if (root.dataset.ctaMode === "off") { event.preventDefault(); openLead(); } else if (window.matchMedia("(min-width: 900px) and (pointer: fine)").matches) { event.preventDefault(); openModal(callModal); } };
      link.addEventListener("click", handler); cleanups.push(() => link.removeEventListener("click", handler));
    });
    root.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((backdrop) => { const handler = (event: Event) => { if (event.target === backdrop) closeModal(backdrop); }; backdrop.addEventListener("click", handler); cleanups.push(() => backdrop.removeEventListener("click", handler)); });
    const key = (event: KeyboardEvent) => { if (event.key === "Escape") root.querySelectorAll<HTMLElement>(".modal-backdrop.open").forEach(closeModal); };
    document.addEventListener("keydown", key); cleanups.push(() => document.removeEventListener("keydown", key));
    root.querySelectorAll<HTMLFormElement>("form.inline-form-right, #modal-form").forEach((form) => {
      const submit = (event: Event) => {
        event.preventDefault();
        const data = new FormData(form); const name = String(data.get("name") ?? "").trim();
        void notifyForm({ formType: "Piles Doctor Consultation", source: `${city} Piles Doctor Page`, fields: [
          { label: "Name", value: name }, { label: "Phone", value: String(data.get("phone") ?? "").trim() },
          { label: "Treatment", value: "Piles Care" }, { label: "City", value: city }, { label: "Doctor", value: config.doctor }, { label: "Page", value: window.location.href },
        ] });
        const tyName = root.querySelector("#ty-name"); if (tyName) tyName.textContent = (name || "friend").split(" ")[0];
        form.reset(); if (formContainer) formContainer.style.display = "none"; thankYou?.classList.add("active"); openModal(leadModal);
      };
      form.addEventListener("submit", submit); cleanups.push(() => form.removeEventListener("submit", submit));
    });
    const mapFrame = root.querySelector<HTMLElement>("#map-frame");
    if (mapFrame && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || mapFrame.querySelector("iframe")) return;
        const src = mapFrame.dataset.mapSrc; if (!src) return;
        const iframe = document.createElement("iframe"); iframe.src = src; iframe.loading = "lazy"; iframe.title = `Healic Multispeciality Hospital ${city} map`; iframe.referrerPolicy = "no-referrer-when-downgrade"; mapFrame.appendChild(iframe); observer.disconnect();
      }, { rootMargin: "300px" });
      observer.observe(mapFrame); cleanups.push(() => observer.disconnect());
    }
    return () => { cleanups.forEach((cleanup) => cleanup()); document.documentElement.classList.remove("modal-open"); };
  }, [city, config.doctor]);

  return <>
    <style dangerouslySetInnerHTML={{ __html: PILES_DOCTOR_CSS + LP_CHROME_CSS + PAGE_FIXES }} />
    <div className="lp-chrome"><SiteHeader /></div>
    <div ref={rootRef} className="lp-piles-doctor" data-cta-mode={ctaMode} dangerouslySetInnerHTML={{ __html: html }} />
    <div className="lp-chrome"><SiteFooter /></div>
    <section className="lp-piles-doctor lp-disclaimer-wrap"><div className="wrap"><div className="ft-disc">Results vary by individual. Piles treatment and surgery carry risks that your surgeon will explain during consultation. This page is educational and is not a substitute for medical advice.</div></div></section>
  </>;
}
